import React, { useRef } from 'react';
import EntityMultiSelect from '../../../../shared/components/EntityMultiSelect';
import { relationEntityIds, replaceEntityRelations } from '../../../../shared/utils/entitySelection';
import { PartyField } from '../../../../shared/components/PartyFormFields';
import { CreateFormDialog, CreateFormCancelButton, CreateFormSubmitButton } from '../../../../shared/components/CreateFormDialog';
import IconAction from '../../../../shared/components/IconAction';
import { FileText, Upload } from '../../../../shared/assets/AppIcons';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { DOCUMENT_CATEGORIES } from '../../utils/documents.constants';
import '../../styles/document-upload-form.css';

export default function UploadDocumentDialog({
  open, onOpenChange, clients = [], staff = [], clientsUnavailable = false, staffUnavailable = false,
  dragActive, isUploading = false, uploadedFile, form, error = '', onFieldChange,
  onFileInput, onDrag, onDrop, onRemoveFile, onSubmit, onCancel,
  editing = false, extraFields, comments, categories = [],
}) {
  const inputRef = useRef(null);

  return <CreateFormDialog open={open} onOpenChange={onOpenChange} icon={FileText}
    title={editing ? "Modifica documento" : "Carica Documento"} busy={isUploading} className="party-form-dialog document-upload-dialog"
    footer={<>
      <CreateFormCancelButton onClick={onCancel} disabled={isUploading} />
      <CreateFormSubmitButton saving={isUploading} savingLabel={editing ? "Salvataggio..." : "Caricamento..."} form="document-upload-form">{editing ? "Salva documento" : "Carica Documento"}</CreateFormSubmitButton>
    </>}>
    <form id="document-upload-form" className="party-form" onSubmit={(event) => {
      event.preventDefault();
      if (!isUploading) onSubmit();
    }}>
      <div className="party-form__row">
        <PartyField id="upload-document-title" label="Titolo" required maxLength={255} disabled={isUploading}
          value={form.name} onChange={(value) => onFieldChange('name', value)} placeholder="Esempio: Contratto di Servizio ACME 2024" />
        <div className="party-form__field">
          <Label htmlFor="upload-document-category">Categoria</Label>
          <Select value={form.category} onValueChange={(value) => { if (value) onFieldChange('category', value); }} disabled={isUploading}>
            <SelectTrigger id="upload-document-category"><SelectValue placeholder="Seleziona categoria…" /></SelectTrigger>
            <SelectContent>{[...new Set([...DOCUMENT_CATEGORIES, ...categories.filter(category => category !== 'Tutti'), form.category].filter(Boolean))].map(category => <SelectItem key={category} value={category}>{category}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <PartyField id="upload-document-description" label="Descrizione" multiline rows={3} maxLength={4000}
        value={form.description} onChange={(value) => onFieldChange('description', value)} disabled={isUploading}
        placeholder="Aggiungi brevi note o dettagli sul documento caricato…" />

      <div className="party-form__field">
        <Label htmlFor={editing ? undefined : "document-upload-browse"}>{editing ? "File archiviato" : "File *"}</Label>
        {!editing && <>
        <input id="file-upload" ref={inputRef} type="file" hidden disabled={isUploading}
          aria-label="Seleziona file documento" accept=".pdf,.png,.jpg,.jpeg,.docx,.doc,.xls,.xlsx"
          onChange={(event) => { onFileInput(event); event.target.value = ''; }} />
        <button id="document-upload-browse" type="button" aria-label={uploadedFile ? 'Sostituisci file' : 'Seleziona file'}
          className={`document-upload__dropzone${dragActive ? ' is-dragging' : ''}`} disabled={isUploading}
          aria-describedby="document-upload-help" onClick={() => inputRef.current?.click()}
          onDragEnter={onDrag} onDragOver={onDrag} onDragLeave={onDrag} onDrop={onDrop}>
          <Upload size={30} aria-hidden="true" />
          <span>{uploadedFile ? 'Trascina un altro file qui o clicca per sostituirlo' : 'Trascina il file qui o clicca per caricare'}</span>
          <small id="document-upload-help">PDF, PNG, JPG, DOCX e altri formati · Max 10 MB</small>
        </button>
        </>}
        {uploadedFile ? <div className="document-upload__file" role="status">
          <FileText size={24} aria-hidden="true" />
          <span className="document-upload__file-name">{uploadedFile.name}{Number(uploadedFile.size) > 0 && <small>{new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 }).format(uploadedFile.size / 1000)} KB</small>}</span>
          {!editing && <IconAction iconKey="delete" tone="danger" label="Rimuovi file" disabled={isUploading} onClick={onRemoveFile} />}
        </div> : null}
        {error ? <p role="alert" className="document-upload__error wt-inline-error">{error}</p> : null}
      </div>

      <div className="party-form__row">
        <div className="party-form__field">
          <Label htmlFor="upload-document-clients">Cliente *</Label>
          <EntityMultiSelect id="upload-document-clients" label="Clienti" required options={clients}
            disabled={isUploading || clientsUnavailable} value={relationEntityIds(form, 'CLIENT')}
            onChange={(ids) => {
              onFieldChange('clientId', ids[0] || '');
              onFieldChange('relations', replaceEntityRelations(form.relations, 'CLIENT', ids, clients));
            }} />
          {clientsUnavailable ? <p role="alert" className="document-upload__error wt-inline-error">Catalogo clienti non disponibile. Riprova tra poco.</p> : null}
        </div>
        <div className="party-form__field">
          <Label htmlFor="upload-document-staff">Personale</Label>
          <EntityMultiSelect id="upload-document-staff" label="Personale" options={staff}
            disabled={isUploading || staffUnavailable} value={relationEntityIds(form, 'TEAM_MEMBER')}
            onChange={(ids) => onFieldChange('staffIds', ids)} />
          {staffUnavailable ? <p role="alert" className="document-upload__error wt-inline-error">Catalogo personale non disponibile. Riprova tra poco.</p> : null}
        </div>
      </div>

      <div className="party-form__row">
        <PartyField id="upload-document-date" label="Data documento" type="date" disabled={isUploading}
          value={form.documentDate} onChange={(value) => onFieldChange('documentDate', value)} />
        <PartyField id="upload-document-due-date" label="Scadenza" type="date" disabled={isUploading}
          value={form.dueDate} onChange={(value) => onFieldChange('dueDate', value)} />
      </div>
      {extraFields}
    </form>
    {comments}
  </CreateFormDialog>;
}
